
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
  } from "react";
  
  
import {
    Crosshair,
    ChartNoAxesColumn,
    Waves,
    CandlestickChart as CandlestickIcon,
  } from "lucide-react";
  
  
  import { useNavigate, useSearchParams } from "react-router-dom";
  import "./Charts.css";
  import CandlestickChart from "./CandlestickChart";
  import websocket from "../services/websocket";
  import ForexBottomNav from "../Components/ForexBottomNav";
  import ForexHeader from "./ForexHeader";
  const candleCache = new Map();
  
  const PAIRS = [
    { value: "EURUSD", label: "EUR/USD" },
    { value: "GBPUSD", label: "GBP/USD" },
    { value: "USDJPY", label: "USD/JPY" },
    { value: "AUDUSD", label: "AUD/USD" },
    { value: "USDCAD", label: "USD/CAD" },
    { value: "USDCHF", label: "USD/CHF" },
    { value: "NZDUSD", label: "NZD/USD" },
    { value: "EURGBP", label: "EUR/GBP" },
    { value: "EURJPY", label: "EUR/JPY" },
    { value: "GBPJPY", label: "GBP/JPY" },
    { value: "EURAUD", label: "EUR/AUD" },
    { value: "EURCHF", label: "EUR/CHF" },
    { value: "EURCAD", label: "EUR/CAD" },
    { value: "EURNZD", label: "EUR/NZD" },
    { value: "GBPAUD", label: "GBP/AUD" },
    { value: "GBPCAD", label: "GBP/CAD" },
    { value: "GBPCHF", label: "GBP/CHF" },
    { value: "GBPNZD", label: "GBP/NZD" },
    { value: "AUDJPY", label: "AUD/JPY" },
    { value: "AUDCAD", label: "AUD/CAD" },
    { value: "AUDCHF", label: "AUD/CHF" },
    { value: "AUDNZD", label: "AUD/NZD" },
    { value: "CADJPY", label: "CAD/JPY" },
    { value: "CADCHF", label: "CAD/CHF" },
    { value: "CHFJPY", label: "CHF/JPY" },
    { value: "NZDJPY", label: "NZD/JPY" },
    { value: "NZDCAD", label: "NZD/CAD" },
    { value: "NZDCHF", label: "NZD/CHF" },
  ];
  
  const TradingView = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
  
    const urlSymbol = searchParams.get("symbol") || "EURUSD";
  
    const symbol = PAIRS.some((pair) => pair.value === urlSymbol)
      ? urlSymbol
      : "EURUSD";
  
    const [timeframe, setTimeframe] = useState("5m");
    const [chartType, setChartType] = useState("candles");
    const [showOHLC, setShowOHLC] = useState(false);
    const [price, setPrice] = useState("--");
    const [candles, setCandles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCrosshair, setShowCrosshair] = useState(true);
  
    const timeframes = useMemo(
      () => [
        { label: "1m", seconds: 60 },
        { label: "5m", seconds: 300 },
        { label: "15m", seconds: 900 },
        { label: "30m", seconds: 1800 },
        { label: "1H", seconds: 3600 },
        { label: "4H", seconds: 14400 },
        { label: "1D", seconds: 86400 },
        { label: "1W", seconds: 86400, rangeDays: 7 },
        { label: "2W", seconds: 86400, rangeDays: 14 },
        { label: "1M", seconds: 86400, rangeDays: 30 },
        { label: "2M", seconds: 86400, rangeDays: 60 },
        { label: "1Y", seconds: 86400, rangeDays: 365 },
      ],
      []
    );
  
    const selectedTimeframe = useMemo(
      () =>
        timeframes.find((item) => item.label === timeframe) ||
        timeframes[1],
      [timeframe, timeframes]
    );
  
    const handlePairChange = (event) => {
      const nextSymbol = event.target.value;
      const nextParams = new URLSearchParams(searchParams);
  
      nextParams.set("symbol", nextSymbol);
      setSearchParams(nextParams);
    };
  
    const formatPrice = useCallback(
      (value) => {
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          !Number.isFinite(Number(value))
        ) {
          return "--";
        }
  
        const digits = symbol.endsWith("JPY") ? 3 : 5;
        return Number(value).toFixed(digits);
      },
      [symbol]
    );
  
    const visibleCandles = useMemo(() => {
      if (!candles.length) return [];
  
      if (!selectedTimeframe.rangeDays) {
        return candles;
      }
  
      const cutoff =
        Math.floor(Date.now() / 1000) -
        selectedTimeframe.rangeDays * 86400;
  
      const filtered = candles.filter(
        (candle) => candle.time >= cutoff
      );
  
      return filtered.length ? filtered : candles;
    }, [candles, selectedTimeframe]);
  
    const marketAnalysis = useMemo(() => {
      if (!visibleCandles.length) {
        return {
          trend: "Analyzing...",
          structure: "--",
          lastHigh: null,
          lastLow: null,
        };
      }
  
      const valid = visibleCandles.filter(
        (candle) =>
          Number.isFinite(candle.high) &&
          Number.isFinite(candle.low) &&
          Number.isFinite(candle.close)
      );
  
      if (!valid.length) {
        return {
          trend: "Analyzing...",
          structure: "--",
          lastHigh: null,
          lastLow: null,
        };
      }
  
      const lastHigh = Math.max(
        ...valid.map((candle) => candle.high)
      );
  
      const lastLow = Math.min(
        ...valid.map((candle) => candle.low)
      );
  
      const firstClose = valid[0].close;
      const lastClose = valid[valid.length - 1].close;
      const change = lastClose - firstClose;
  
      const threshold = Math.max(
        Math.abs(firstClose) * 0.00005,
        0.00001
      );
  
      let trend = "Sideways";
  
      if (change > threshold) {
        trend = "Bullish";
      } else if (change < -threshold) {
        trend = "Bearish";
      }
  
      const midpoint = Math.max(
        1,
        Math.floor(valid.length / 2)
      );
  
      const previous = valid.slice(0, midpoint);
      const recent = valid.slice(midpoint);
  
      let structure = "Range";
  
      if (previous.length && recent.length) {
        const previousHigh = Math.max(
          ...previous.map((candle) => candle.high)
        );
  
        const recentHigh = Math.max(
          ...recent.map((candle) => candle.high)
        );
  
        const previousLow = Math.min(
          ...previous.map((candle) => candle.low)
        );
  
        const recentLow = Math.min(
          ...recent.map((candle) => candle.low)
        );
  
        if (
          recentHigh > previousHigh &&
          recentLow > previousLow
        ) {
          structure = "Higher High / Higher Low";
        } else if (
          recentHigh < previousHigh &&
          recentLow < previousLow
        ) {
          structure = "Lower High / Lower Low";
        } else if (recentHigh > previousHigh) {
          structure = "High Breakout";
        } else if (recentLow < previousLow) {
          structure = "Low Breakout";
        }
      }
  
      return {
        trend,
        structure,
        lastHigh,
        lastLow,
      };
    }, [visibleCandles]);
  
    useEffect(() => {
      let active = true;
  
      const apiSymbol = "frx" + symbol;
      const granularity = selectedTimeframe.seconds;
      const cacheKey = `${apiSymbol}-${granularity}`;
  
      setPrice("--");
  
      const cached = candleCache.get(cacheKey);
  
      if (cached && cached.length) {
        setCandles(cached);
  
        const last = cached[cached.length - 1];
  
        if (last) {
          setPrice(formatPrice(last.close));
        }
  
        setLoading(false);
      } else {
        setCandles([]);
        setLoading(true);
      }
  
      websocket.connect();
  
      const unsubscribe = websocket.onTick((data) => {
        if (!active) return;
  
        if (data.error) {
          console.error(
            "Chart error:",
            data.error.code,
            data.error.message
          );
  
          setLoading(false);
          return;
        }
  
        // Historical candles
        if (data.candles && data.echo_req?.ticks_history) {
          const responseSymbol = data.echo_req.ticks_history;
  
          const responseGranularity = Number(
            data.echo_req?.granularity
          );
  
          if (responseSymbol !== apiSymbol) return;
  
          if (
            Number.isFinite(responseGranularity) &&
            responseGranularity !== granularity
          ) {
            return;
          }
  
          const formatted = data.candles
            .map((candle) => ({
              time: Number(candle.epoch),
              open: Number(candle.open),
              high: Number(candle.high),
              low: Number(candle.low),
              close: Number(candle.close),
            }))
            .filter(
              (candle) =>
                Number.isFinite(candle.time) &&
                Number.isFinite(candle.open) &&
                Number.isFinite(candle.high) &&
                Number.isFinite(candle.low) &&
                Number.isFinite(candle.close)
            )
            .sort((a, b) => a.time - b.time);
  
          if (!formatted.length) {
            setLoading(false);
            return;
          }
  
          const unique = [];
  
          formatted.forEach((candle) => {
            const previous = unique[unique.length - 1];
  
            if (previous && previous.time === candle.time) {
              unique[unique.length - 1] = candle;
            } else {
              unique.push(candle);
            }
          });
  
          const finalCandles =
            unique.length > 150 ? unique.slice(-150) : unique;
  
          candleCache.set(cacheKey, finalCandles);
          setCandles(finalCandles);
  
          const last = finalCandles[finalCandles.length - 1];
  
          if (last) {
            setPrice(formatPrice(last.close));
          }
  
          setLoading(false);
          return;
        }
  
        // Live OHLC candle
        if (data.ohlc) {
          const ohlc = data.ohlc;
  
          const responseSymbol =
            ohlc.symbol || ohlc.underlying_symbol || "";
  
          if (responseSymbol !== apiSymbol) return;
  
          const liveCandle = {
            time: Number(ohlc.open_time || ohlc.epoch),
            open: Number(ohlc.open),
            high: Number(ohlc.high),
            low: Number(ohlc.low),
            close: Number(ohlc.close),
          };
  
          if (
            !Number.isFinite(liveCandle.time) ||
            !Number.isFinite(liveCandle.open) ||
            !Number.isFinite(liveCandle.high) ||
            !Number.isFinite(liveCandle.low) ||
            !Number.isFinite(liveCandle.close)
          ) {
            return;
          }
  
          setPrice(formatPrice(liveCandle.close));
  
          setCandles((oldCandles) => {
            let updated = [...oldCandles];
  
            if (!updated.length) {
              updated = [liveCandle];
            } else {
              const lastIndex = updated.length - 1;
              const lastCandle = updated[lastIndex];
  
              if (lastCandle.time === liveCandle.time) {
                updated[lastIndex] = liveCandle;
              } else if (liveCandle.time > lastCandle.time) {
                updated.push(liveCandle);
              }
  
              if (updated.length > 150) {
                updated = updated.slice(-150);
              }
            }
  
            candleCache.set(cacheKey, updated);
            return updated;
          });
  
          setLoading(false);
          return;
        }
  
        // Live tick price
        if (data.tick) {
          const tickSymbol =
            data.tick.symbol ||
            data.tick.underlying_symbol ||
            "";
  
          if (tickSymbol !== apiSymbol) return;
  
          const currentPrice = Number(data.tick.quote);
  
          if (Number.isFinite(currentPrice)) {
            setPrice(formatPrice(currentPrice));
          }
        }
      });
  
      websocket.candles(apiSymbol, granularity);
  
      return () => {
        active = false;
        unsubscribe();
      };
    }, [symbol, selectedTimeframe.seconds, formatPrice]);
  
    const handleSell = () => {
      if (price === "--") return;
  
      navigate("/confirm-order", {
        state: {
          symbol,
          side: "sell",
          price: Number(price),
        },
      });
    };
  
    const handleBuy = () => {
      if (price === "--") return;
  
      navigate("/confirm-order", {
        state: {
          symbol,
          side: "buy",
          price: Number(price),
        },
      });
    };
  
    return (
        <div className="forex-chart-page">
            <ForexHeader/>
        <ForexBottomNav/>
        {/* HEADER AND PAIR SELECTOR */}
        <div className="tv-header">
          <button
            type="button"
            className="tv-back-btn"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ‹
          </button>
  
          <div className="symbol-info">
            <h2>{symbol}</h2>
            <span>Forex Pair</span>
          </div>
  
          <select
            className="pair-selector"
            aria-label="Select Forex Pair"
            value={symbol}
            onChange={handlePairChange}
          >
            {PAIRS.map((pair) => (
              <option key={pair.value} value={pair.value}>
                {pair.label}
              </option>
            ))}
          </select>
  
          <div className="current-price">{price}</div>
        </div>
  
        {/* TIMEFRAMES */}
        <div className="timeframes">
          {timeframes.map((item) => (
            <button
              type="button"
              key={item.label}
              className={timeframe === item.label ? "active" : ""}
              onClick={() => setTimeframe(item.label)}
            >
              {item.label}
            </button>
          ))}
        </div>
  
        {/* CHART */}
        <div className="main-chart">
          <div className="chart-toolbar">
            <div className="chart-title">
              <span>{symbol}</span>
              <span>{timeframe}</span>
            </div>
  
            
<div className="chart-type-controls">
  <button
    type="button"
    title="Crosshair"
    aria-label="Crosshair"
    className={showCrosshair ? "active" : ""}
    onClick={() => setShowCrosshair((value) => !value)}
  >
    <Crosshair size={28} strokeWidth={1.8} />
  </button>

  <button
    type="button"
    title="Candlestick chart"
    aria-label="Candlestick chart"
    className={chartType === "candles" ? "active" : ""}
    onClick={() => setChartType("candles")}
  >
    <CandlestickIcon size={28} strokeWidth={1.8} />
  </button>

  <button
    type="button"
    title="Bar chart"
    aria-label="Bar chart"
    className={chartType === "bars" ? "active" : ""}
    onClick={() => setChartType("bars")}
  >
    <ChartNoAxesColumn size={28} strokeWidth={1.8} />
  </button>

  <button
    type="button"
    title="Area chart"
    aria-label="Area chart"
    className={chartType === "area" ? "active" : ""}
    onClick={() => setChartType("area")}
  >
    <Waves size={28} strokeWidth={1.8} />
  </button>

  <button
    type="button"
    title="OHLC"
    aria-label="Toggle OHLC"
    className={`ohlc-button ${showOHLC ? "active" : ""}`}
    onClick={() => setShowOHLC((value) => !value)}
  >
    OHLC
  </button>
</div>

  
            <span className="live-status">● LIVE</span>
          </div>
  
          <div className="candlestick-container">
            <CandlestickChart
              candles={visibleCandles}
              timeframe={timeframe}
              chartType={chartType}
              showOHLC={showOHLC}
              showCrosshair={showCrosshair}
            />
  
            {loading && visibleCandles.length === 0 && (
              <div className="chart-loading-overlay">
                Loading {symbol}...
              </div>
            )}
          </div>
  
          {/* BUY AND SELL */}
          <div className="quick-trade-row">
            <button
              type="button"
              className="quick-trade-btn quick-sell"
              disabled={price === "--"}
              onClick={handleSell}
            >
              <span>SELL</span>
              <strong>{price}</strong>
            </button>
  
            <button
              type="button"
              className="quick-trade-btn quick-buy"
              disabled={price === "--"}
              onClick={handleBuy}
            >
              <span>BUY</span>
              <strong>{price}</strong>
            </button>
          </div>
        </div>
  
        {/* MARKET STRUCTURE */}
        <div className="market-structure">
          <div className="structure-header">
            <h3>Market Structure</h3>
            <span>{timeframe}</span>
          </div>
  
          <div className="structure-grid">
            <div className="structure-box">
              <span>Trend</span>
              <strong
                className={`structure-value ${marketAnalysis.trend.toLowerCase()}`}
              >
                {marketAnalysis.trend}
              </strong>
            </div>
  
            <div className="structure-box">
              <span>Structure</span>
              <strong className="structure-value">
                {marketAnalysis.structure}
              </strong>
            </div>
  
            <div className="structure-box">
              <span>Last High</span>
              <strong className="structure-value high-value">
                {formatPrice(marketAnalysis.lastHigh)}
              </strong>
            </div>
  
            <div className="structure-box">
              <span>Last Low</span>
              <strong className="structure-value low-value">
                {formatPrice(marketAnalysis.lastLow)}
              </strong>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  export default TradingView;
  